import { type fabric } from "fabric"
import { isSpotColor, spotColorName } from "../spot_colors"
import { arrayUniq } from "@/signomatic/util"

export type DrawableClasses =
  | "base"
  | "bottom"
  | "preview"
  | "overlay"
  | "pattern"
  | "cutline"
  | "gapped"
  | "bevel"
  | "border"
  | "withsnap"
  | "withoutsnap"
  | "snappable"
  | "pdfoverflow"
  | "hang"
  | "stand"
  | "stretch"
  | "eyelethole"
  | "objectborder"
  | "wall_distance"
  | "suction_cups"
  | "pole"
  | "hanging"
  | "keyring"
  | "screw"
  | "flangedhole"
  | "shadowbase"
  | "borderdouble"
  | "flangedpath"
  | "printareacutline"
  | "offsetdrawable"
  | "glassborder"

export interface DrawableMixin extends fabric.Object {
  classes: DrawableClasses[]
  offsetX: number
  offsetY: number
  spotFill: string | null
  spotStroke: string | null

  /**
   * Adds css class to be used in SVG
   */
  addClass(...classNames: DrawableClasses[]): this

  /**
   * Remove all existing CSS classes
   */
  clearClasses(): this

  /**
   * Disable adding any CSS classes in this object's SVG
   */
  disableClasses(disable: boolean): this
}

interface MaybeSvg extends fabric.Object {
  _createBaseSVGMarkup(objectMarkup: string[], options: any): string
}

export default function DrawableMixin<T extends Constructor<MaybeSvg>>(Base: T) {
  return class extends Base implements DrawableMixin {
    private classList: DrawableClasses[] = []
    private classesDisabled = false
    offsetX = 0
    offsetY = 0
    spotFill = null
    spotStroke = null

    get classes() {
      if (this.classesDisabled) {
        return []
      }

      return this.classList
    }

    // https://github.com/fabricjs/fabric.js/blob/v460/src/mixins/object.svg_export.js#L197
    _createBaseSVGMarkup(objectMarkup: string[], options: any) {
      if (this.classList.length > 0) {
        const index = objectMarkup.indexOf("COMMON_PARTS")

        if (index >= 0) {
          const attrs = [`class="${arrayUniq(this.classList).join(" ")}"`]

          const autoSpot =
            this.classes.includes("base") ||
            this.classes.includes("border") ||
            this.classes.includes("objectborder") ||
            this.classes.includes("pdfoverflow")

          if (this.spotFill) {
            attrs.push(`spotfill="${this.spotFill}"`)
          } else if (typeof this.fill === "string" && isSpotColor(this.fill) && autoSpot) {
            attrs.push(`spotfill="${spotColorName(this.fill)}"`)
          }

          if (this.spotStroke) {
            attrs.push(`spotstroke="${this.spotStroke}"`)
          } else if (typeof this.stroke === "string" && isSpotColor(this.stroke) && autoSpot) {
            attrs.push(`spotstroke="${spotColorName(this.stroke)}"`)
          }

          objectMarkup.splice(index, 0, `${attrs.join(" ")} `)
        }
      }

      return super._createBaseSVGMarkup(objectMarkup, options)
    }

    addClass(...classNames: DrawableClasses[]) {
      this.classList.push(...classNames)
      return this
    }

    clearClasses() {
      this.classList.splice(0)
      return this
    }

    disableClasses(disable: boolean) {
      this.classesDisabled = disable
      return this
    }
  }
}
